import chroma from "chroma-js";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { styled } from "../stitches.config";

type Props = Partial<{
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (value: number) => void;
  onMouseDown: (value: number) => void;
  onMouseUp: (value: number) => void;
  onPointed: (value: number) => void;
  containerStyle: React.CSSProperties;
  themeColor: string;
  showPointed: boolean;
}>;

export const Slider = React.forwardRef<HTMLInputElement, Props>(function Slider(
  props,
  ref
) {
  const min = props.min ?? 0;
  const max = props.max ?? 100;
  const step = props.step ?? 1;
  const [value, setValue] = useState(props.value ?? 50);
  useEffect(() => {
    setValue(props.value ?? 50);
  }, [props.value]);

  const onChange = useMemo(
    () => props.onChange || function () {},
    [props.onChange]
  );
  const onMouseDown = useMemo(
    () => props.onMouseDown || function () {},
    [props.onMouseDown]
  );
  const onMouseUp = useMemo(
    () => props.onMouseUp || function () {},
    [props.onMouseUp]
  );
  const onPointed = useMemo(
    () => props.onPointed || function () {},
    [props.onPointed]
  );

  const customSliderRef = useRef<HTMLDivElement>(null);
  const [pointedValue, setPointedValue] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const getValue = useCallback(
    (e: React.MouseEvent<HTMLDivElement> | MouseEvent) => {
      if (!customSliderRef.current) return 0;
      const containerRect = customSliderRef.current.getBoundingClientRect();
      const containerLeft = containerRect.left;
      const containerWidth = containerRect.width;
      const pointerX = e.clientX;
      const value = max * ((pointerX - containerLeft) / containerWidth);
      return Math.max(min, Math.min(value, max));
    },
    [min, max]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const newValue = getValue(e);
      setIsDragging(true);
      onMouseDown(newValue);
      onChange(newValue);
    },
    [getValue, onMouseDown, onChange]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const newValue = getValue(e);
      setPointedValue(newValue);
      onPointed(newValue);
      if (isDragging) {
        onChange(newValue);
      }
    },
    [isDragging, getValue, onPointed, onChange]
  );

  const handleMouseLeave = useCallback(() => {
    setPointedValue(0);
    onPointed(0);
  }, [onPointed]);

  /** Handle input change from accessibility devices or keyboards. */
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = parseFloat(e.target.value);
      setValue(newValue);
      onChange(newValue);
    },
    [onChange]
  );

  /** Allow mouse go out of the input area while dragging. */
  useEffect(() => {
    function up() {
      setIsDragging(false);
      onMouseUp(value);
    }

    function move(e: MouseEvent) {
      const newValue = getValue(e);
      onChange(newValue);
    }

    if (isDragging) {
      window.addEventListener("mouseup", up);
      window.addEventListener("mousemove", move);

      return () => {
        window.removeEventListener("mouseup", up);
        window.removeEventListener("mousemove", move);
      };
    }
  }, [isDragging, onMouseUp, onChange, value, getValue]);

  const ThemedThumb = useMemo(() => {
    const themeColor = props.themeColor || "#fff";
    return styled(Thumb, {
      background: themeColor,
      "&:hover::after": {
        background: chroma(themeColor).alpha(0.1),
      },
    });
  }, [props.themeColor]);

  return (
    <Container style={props.containerStyle}>
      <AccessibleInput
        ref={ref}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={handleInputChange}
      />
      <CustomSliderWrapper>
        <CustomSlider
          ref={customSliderRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          data-cursor={"pointer"}
        >
          <Track />
          {props.showPointed && (
            <PointedTrack
              style={{ width: `${toPercentString(pointedValue, max)}` }}
            />
          )}
          <FilledTrack
            style={{
              width: `${toPercentString(value, max)}`,
              background: props.themeColor || "#fff",
            }}
          />
          <ThemedThumb
            style={{
              left: `${toPercentString(value, max)}`,
            }}
          />
        </CustomSlider>
      </CustomSliderWrapper>
    </Container>
  );
});

const Container = styled("div", {
  position: "relative",
  width: "100%",
  height: "0.75rem",
});

const CustomSliderWrapper = styled("div", {
  padding: "0 0.375rem",
  height: "100%",
});

const CustomSlider = styled("div", {
  position: "relative",
  width: "100%",
  height: "100%",
  cursor: "pointer",
});

const Thumb = styled("div", {
  width: "0.75rem",
  height: "0.75rem",
  borderRadius: "100%",
  background: "#fff",
  boxShadow: "inset 0px -3px 0px rgba(113, 81, 78, 0.2)",
  position: "absolute",
  transform: "translateX(-0.375rem)",
  "&:hover::after": {
    content: "",
    position: "absolute",
    width: "100%",
    height: "100%",
    borderRadius: "100%",
    background: chroma("#fff").alpha(0.1),
  },
});

const Track = styled("div", {
  height: 2,
  background: "rgba(255, 255, 255, 0.1)",
  borderRadius: 1,
  position: "absolute",
  top: "calc(($3 - 2px) / 2)",
  left: 0,
  right: 0,
});

const FilledTrack = styled(Track, { background: "#fff" });
const PointedTrack = styled(Track, { background: "rgba(255, 255, 255, 0.35)" });

const AccessibleInput = styled("input", {
  width: "100%",
  height: "100%",
  margin: 0,
  padding: 0,
  border: "none",
  background: "transparent",
  color: "transparent",
  overflow: "visible",
  position: "absolute",
  top: 0,
  left: 0,
  "-webkit-appearance": "none",
  "&::-webkit-slider-thumb": {
    "-webkit-appearance": "none",
  },
  "&::-webkit-slider-track": {
    "-webkit-appearance": "none",
  },
  "&::-moz-range-thumb": {
    border: "none",
    background: "transparent",
  },
});

function toPercentString(value: number, total: number) {
  return `${(value / total) * 100}%`;
}
